"use client";

import React from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useClientAuth } from "@/app/providers/client-auth";
import PageLoaderOverlay from "@/components/page-loader-overlay";

export function AuthGate({
  children,
  loadingFallback,
  redirectBase = "/login",
}: {
  children: React.ReactNode;
  loadingFallback?: React.ReactNode;
  redirectBase?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { status } = useClientAuth();

  React.useEffect(() => {
    if (status !== "unauthenticated") {
      return;
    }
    const query = searchParams?.toString();
    const destination = pathname ? `${pathname}${query ? `?${query}` : ""}` : "/";
    const redirectUrl = `?redirect_url=${encodeURIComponent(destination)}`;
    router.replace(`${redirectBase}${redirectUrl}`);
  }, [pathname, redirectBase, router, searchParams, status]);

  if (status === "loading" || status === "unauthenticated") {
    return <>{loadingFallback ?? <PageLoaderOverlay message="Loading..." opacity="dark" />}</>;
  }

  if (status === "error") {
    return <div className="p-4 text-sm text-red-500">Failed to load auth state.</div>;
  }

  return <>{children}</>;
}
